import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import EntityStatusPicker from '../EntityStatusPicker'

/**
 * ConfirmationForm - Form for confirming entity sightings
 * 
 * Features:
 * - Notes textarea
 * - Image upload (requires 1+ images)
 * - Submit and cancel buttons
 * - Loading state
 * 
 * @param {Object} props
 * @param {string} props.confirmNotes - Confirmation notes
 * @param {Function} props.setConfirmNotes - Set notes handler
 * @param {Array} props.confirmImages - Uploaded images
 * @param {Function} props.setConfirmImages - Set images handler
 * @param {boolean} props.isSubmitting - Submitting state
 * @param {Component} props.ImageUpload - Image upload component
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * 
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render confirmation form
 */
const ConfirmationForm = memo(function ConfirmationForm({
  confirmNotes,
  setConfirmNotes,
  confirmImages,
  setConfirmImages,
  entityStatus = null,
  onEntityStatusChange = null,
  isSubmitting,
  ImageUpload,
  onSubmit,
  onCancel
}) {
  const { t } = useTranslation()
  return (
    <div className="pt-3 border-t border-gray-200/50">
      <div className="space-y-2">
        {onEntityStatusChange && (
          <EntityStatusPicker value={entityStatus} onChange={onEntityStatusChange} />
        )}

        <div>
          <ImageUpload
            images={confirmImages}
            setImages={setConfirmImages}
            maxImages={1}
            disabled={isSubmitting}
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={onCancel}
            onTouchEnd={(e) => { e.preventDefault(); onCancel(e) }}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center gap-2 text-sm bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('app.cancel', 'Cancel')}
          </button>
          <button
            onClick={onSubmit}
            onTouchEnd={(e) => { e.preventDefault(); onSubmit(e) }}
            disabled={isSubmitting || confirmImages.length === 0}
            className="flex-1 px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center gap-2 text-sm bg-primary hover:bg-primary-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('app.loading', 'Sending...')}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('app.submit', 'Submit')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
})

export default ConfirmationForm
